import type { Query } from '@tanstack/react-query';
import { queryClient } from '../constants.js';
import { getAffectedRelations, loadRelationDependencies, qualify } from './dependencyGraph';
import type { QualifiedRelation } from './dependencyGraph';
import { getMatchingCustomQueryKeys } from './queryKeyRegistry';

/**
 * Live sync: turns live change broadcasts (see ./liveChanges.ts) into React Query
 * cache invalidations, so every surface in the app (workspace pages, boards, side panels,
 * admin screens) refreshes without a manual reload.
 *
 * Grids and the record detail form additionally consume the per-table CustomEvents
 * dispatched by liveChanges.ts. Those apply surgical row updates and are unaffected
 * by this module.
 *
 * Changes are coalesced over a short window because a single user action commonly writes
 * several rows (submission + merchant + audit) and each write would otherwise trigger its
 * own refetch wave. Flushes are also spaced at least MIN_FLUSH_INTERVAL_MS apart, so a
 * bulk write lasting minutes costs each tab one refetch wave per interval, not one per
 * statement.
 */

const FLUSH_DELAY_MS = 250;
const MIN_FLUSH_INTERVAL_MS = 2000;

export interface LiveChange {
  schema: string;
  table: string;
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
}

const pendingRelations = new Set<QualifiedRelation>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let lastFlushAt = 0;

const isPostgrestKey = (key: readonly unknown[]): boolean => key[0] === 'postgrest';
const isReportRowsKey = (key: readonly unknown[]): boolean => key[0] === 'report-rows';

/** Cache-helpers key layout: ["postgrest", "null"|"page", schema, relation, ...] */
const postgrestKeyMatches = (key: readonly unknown[], affected: Set<QualifiedRelation>): boolean => {
  const schema = key[2];
  const relation = key[3];
  if (typeof relation !== 'string') return false;
  if (typeof schema === 'string') return affected.has(qualify(schema, relation));
  // Schema unknown on the key — fall back to matching the relation in either CRM schema.
  return affected.has(qualify('public', relation)) || affected.has(qualify('core_crm', relation));
};

const keyStartsWith = (key: readonly unknown[], prefix: readonly (string | number)[]): boolean =>
  prefix.every((part, index) => key[index] === part);

const flush = (): void => {
  flushTimer = null;
  if (pendingRelations.size === 0) return;
  lastFlushAt = Date.now();

  const changed = Array.from(pendingRelations);
  pendingRelations.clear();

  const affected = new Set<QualifiedRelation>();
  changed.forEach((relation) => {
    const [schema, ...rest] = relation.split('.');
    getAffectedRelations(schema, rest.join('.')).forEach((dependent) => affected.add(dependent));
  });

  const customSources = getMatchingCustomQueryKeys(affected);

  void queryClient.invalidateQueries({
    predicate: (query: Query) => {
      const key = query.queryKey;
      if (!Array.isArray(key)) return false;
      if (isReportRowsKey(key)) return true;
      if (isPostgrestKey(key)) return postgrestKeyMatches(key, affected);
      return customSources.some(
        (source) => keyStartsWith(key, source.key) && (source.maxLength === undefined || key.length <= source.maxLength)
      );
    },
    // Only visible queries refetch immediately; everything else refetches when it remounts.
    refetchType: 'active'
  });
};

/**
 * Registers a live change for invalidation.
 */
export const recordLiveChange = ({ schema, table }: LiveChange): void => {
  if (!schema || !table) return;
  pendingRelations.add(qualify(schema, table));
  if (flushTimer === null) {
    const untilNextAllowed = lastFlushAt + MIN_FLUSH_INTERVAL_MS - Date.now();
    flushTimer = setTimeout(flush, Math.max(FLUSH_DELAY_MS, untilNextAllowed));
  }
};

/**
 * Warms the dependency graph so the first change already invalidates derived views.
 */
export const startLiveSync = (): void => {
  void loadRelationDependencies();
};

/** Test helper — flushes immediately instead of waiting for the debounce window. */
export const flushLiveChangesNow = (): void => {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  flush();
};
